import { CircleX } from 'lucide-react';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { useId } from 'react';
import { twMerge } from 'tailwind-merge';

type InputProps = {
	input?: string;
	onInputChange?: (value: string) => void;
	label?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> &
	(
		| {
				customElement: React.ReactNode;
				showClearButton?: never;
		  }
		| {
				customElement?: never;
				showClearButton?: boolean;
		  }
	);

const Input = ({
	input,
	onInputChange,
	label,
	showClearButton = true,
	customElement,
	type = 'text',
	...props
}: InputProps) => {
	const id = useId();
	const hasSearchQuery = !!input && input.length > 0;

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		onInputChange?.(e.currentTarget.value);
	};

	const handleClearButtonClick = () => {
		onInputChange?.('');
	};

	return (
		<div className="flex flex-col items-stretch gap-3">
			{label && (
				<label
					htmlFor={id}
					className="font-medium text-mantang-gray-500 text-xs"
				>
					{label}
				</label>
			)}
			<div className="relative flex basis-[50px] items-center rounded-lg bg-mantang-gray-100 pr-8 pl-3">
				<input
					id={id}
					type={type}
					{...props}
					value={input}
					onChange={handleInputChange}
					className={twMerge(
						'flex-1 font-medium text-base text-mantang-gray-900 outline-none placeholder:font-normal placeholder:text-base placeholder:text-mantang-gray-500',
						type === 'number' && 'number-input-hide-spinner',
					)}
				/>
				{customElement ??
					(hasSearchQuery && showClearButton && (
						<button
							type="button"
							onClick={handleClearButtonClick}
							className="absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer rounded-xl bg-mantang-gray-500"
						>
							<CircleX
								size={18}
								fill="#AFB8C2"
								className="[&_circle]:stroke-[#AFB8C2] [&_path]:stroke-white"
							/>
						</button>
					))}
			</div>
		</div>
	);
};

export default Input;
